// src/components/PropertyAnalysis.tsx

import React, { useMemo, useState } from 'react';
import { Button, Modal, notification } from 'antd';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { comparePropertyWithProperties } from '../../redux/api/documents/propertiesComparisonThunks';

import PropertiesComparisonList from '../PropertiesComparisonList';
// Важно: убедитесь, что Scatter3D и HistogramComparison импортируются
import Scatter3D from '../plots/Scatter3D';
import HistogramComparison from '../plots/DistributionHist';
import Spinner from '../common/Spinner';
import OverlaySpinner from '../common/OverlaySpinner';

import {
    ComparisonResult,
    Document,
    PropertyFull,
    Scatter3DDataItem
} from '../../types';
import ComparePropertyAction from '../property/actions/ComparePropertyAction';
import DeletePropertyAction from '../property/actions/DeletePropertyAction';
import AddPropertyAction from '../property/actions/AddPropertyAction';
import GroupedPropertiesList from '../property/GroupedPropertiesList';
import { selectDocumentsComputing } from '../../redux/api/documents/documentsSelectors';

const PropertyAnalysis: React.FC<{ documentData: Document }> = ({ documentData }) => {
    const dispatch = useAppDispatch();
    const isComputing = useAppSelector(selectDocumentsComputing);

    // Локальное состояние выбранного свойства и результатов сравнения
    const [selectedProperty, setSelectedProperty] = useState<PropertyFull | undefined>(undefined);
    const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);

    // Управление модальными окнами для графика и гистограммы
    const [isGraphModalVisible, setIsGraphModalVisible] = useState<boolean>(false);
    const [isHistogramModalVisible, setIsHistogramModalVisible] = useState<boolean>(false);

    // Все свойства документа
    const allProperties = useMemo(() => documentData.properties ?? [], [documentData.properties]);

    // При выборе свойства запускается сравнение с остальными свойствами текущего документа
    const handleSelectProperty = async (property: PropertyFull) => {
        setSelectedProperty(property);
        try {
            const otherProps = allProperties.filter((p) => p.id !== property.id);
            const action = await dispatch(
                comparePropertyWithProperties({
                    propA: property,
                    otherProps,
                })
            );
            const results = unwrapResult(action);
            setComparisonResults(results);
        } catch (error: any) {
            notification.error({
                message: 'Ошибка при сравнении свойств',
                description: error.message || 'Не удалось выполнить сравнение свойств.',
            });
        }
    };

    // Подготовка данных для 3D-графика (если есть сокращенные векторы)
    const prepareDataForVisualizer: Scatter3DDataItem[] = useMemo(() => {
        return allProperties.reduce<Scatter3DDataItem[]>((acc, prop) => {
            if (prop.reducedVectorA && prop.reducedVectorA.length >= 3) {
                acc.push({
                    x: prop.reducedVectorA[0],
                    y: prop.reducedVectorA[1],
                    z: prop.reducedVectorA[2],
                    text: prop.text,
                    propertyTypeId: prop.propertyTypeId,
                    documentId: prop.documentId,
                });
            }
            return acc;
        }, []);
    }, [allProperties]);

    const handleOpenGraphModal = () => setIsGraphModalVisible(true);
    const handleCloseGraphModal = () => setIsGraphModalVisible(false);
    const handleOpenHistogramModal = () => setIsHistogramModalVisible(true);
    const handleCloseHistogramModal = () => setIsHistogramModalVisible(false);

    if (!documentData.properties) {
        return <Spinner />;
    }

    return (
        <div className="flex flex-wrap relative">
            {/* Левая колонка: список свойств */}
            <div className={selectedProperty && comparisonResults.length > 0 ? 'w-full lg:w-1/2 p-4' : 'w-full p-4'}>
                <div className="p-4 bg-white rounded-lg shadow h-[80vh] overflow-y-auto relative">
                    <h1 className="text-xl font-semibold mb-2">{documentData.title}</h1>

                    <Button type="primary" onClick={handleOpenGraphModal} className="mb-4">
                        Показать график
                    </Button>

                    {selectedProperty && comparisonResults.length > 0 && (
                        <Button type="primary" onClick={handleOpenHistogramModal} className="mb-4 ml-2">
                            Показать гистограмму сходства
                        </Button>
                    )}

                    <GroupedPropertiesList
                        properties={allProperties}
                        selectedProperty={selectedProperty}
                        actions={(property: PropertyFull, isSelected: boolean) => [
                            <ComparePropertyAction
                                key="compare"
                                property={property}
                                isSelected={isSelected}
                                onCompare={handleSelectProperty}
                            />,
                            <DeletePropertyAction
                                key="delete"
                                property={property}
                            />,
                        ]}
                        groupHeaderExtra={(typeId) => (
                            <AddPropertyAction
                                documentId={documentData.id}
                                defaultPropertyTypeId={typeId}
                            />
                        )}
                        showNextButton={true}
                    />
                </div>
            </div>

            {/* Правая колонка: результаты сравнения */}
            <div className="w-full lg:w-1/2 p-4">
                <div className="p-4 bg-white rounded-lg shadow h-[80vh] overflow-y-auto relative">
                    {isComputing && <OverlaySpinner tip="Выполняется вычисление..." />}
                    {selectedProperty && comparisonResults.length > 0 && (
                        <PropertiesComparisonList data={comparisonResults} />
                    )}
                </div>
            </div>

            {/* Модальное окно с 3D-графиком */}
            <Modal
                title="График свойств"
                open={isGraphModalVisible}
                onOk={handleCloseGraphModal}
                onCancel={handleCloseGraphModal}
                footer={[
                    <Button key="back" onClick={handleCloseGraphModal}>
                        Закрыть
                    </Button>,
                ]}
                width="90vw"
                // Добавляем стили, чтобы график мог занять всю высоту внутри модалки
                bodyStyle={{
                    height: '70vh', // или 80vh, на ваше усмотрение
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Внутренний контейнер, который растягивается на 100% */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Scatter3D data={prepareDataForVisualizer} />
                </div>
            </Modal>

            {/* Модальное окно с гистограммой */}
            <Modal
                title="Гистограмма сходства свойств"
                open={isHistogramModalVisible}
                onOk={handleCloseHistogramModal}
                onCancel={handleCloseHistogramModal}
                footer={[
                    <Button key="back" onClick={handleCloseHistogramModal}>
                        Закрыть
                    </Button>,
                ]}
                width="90vw"
                bodyStyle={{
                    height: '70vh',
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {selectedProperty && (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <HistogramComparison
                            selectedProperty={selectedProperty}
                            comparisonResults={comparisonResults}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PropertyAnalysis;
